import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";

import { validateAuthToken } from "@/guards/validateAuthToken";
import { validateActiveUser } from "@/guards/validateActiveUser";
import { formatPatientsListFromConnections } from "@/lib/utils";
import { useAppDispatch, useAppSelector } from "@/app/store";
import {
  getAcceptedConnectionsService,
  resetSelectedConnectionId,
} from "@/app/store/connection/connectionSlice";
import { resetPatientData } from "@/app/store/patient/patientSlice";

import { SectionRoute } from "@/components/shared/SectionRoute";
import { HeadingOne } from "@/components/ui/typography/HeadingOne";
import { PatientsTable } from "@/components/patients/search/table/PatientsTable";
import { searchColumns } from "@/components/patients/search/table/searchColumns";
import { IPatientList } from "@/app/mock-data/getPatientsList";
import { SkeletonTable } from "@/components/shared/skeletons/SkeletonTable";

export const Route = createFileRoute("/dashboard/patients/")({
  beforeLoad: () => {
    validateAuthToken();
    validateActiveUser();
  },
  component: PatientsPage,
});

function PatientsPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { acceptedConnectionList, status } = useAppSelector(
    (state) => state.connection
  );

  useEffect(() => {
    dispatch(resetSelectedConnectionId());
    dispatch(resetPatientData());
    dispatch(getAcceptedConnectionsService());
  }, [dispatch]);

  if (status === "failed") {
    return <Navigate to="/dashboard/error/generic-error" />;
  }

  if (status === "loading" || status === "idle") {
    return (
      <>
        <SectionRoute
          route_one={t("routes.dashboard")}
          route_two={t("routes.patients")}
        />
        <SkeletonTable />
      </>
    );
  }

  if (status === "succeeded") {
    const formattedPatients: IPatientList[] = formatPatientsListFromConnections(
      acceptedConnectionList
    );

    return (
      <>
        <SectionRoute
          route_one={t("routes.dashboard")}
          route_two={t("routes.patients")}
        />
        <div className="mb-8 max-w-7xl mx-auto">
          <div className="my-6">
            <HeadingOne>{t("patients.search.title")}</HeadingOne>
          </div>
          <div className="pb-12">
            <PatientsTable columns={searchColumns} data={formattedPatients} />
          </div>
        </div>
      </>
    );
  }
}

export default PatientsPage;
